import { useEffect } from 'react';
import {
  useQuery, useQueryClient, UseQueryResult, useInfiniteQuery, UseInfiniteQueryResult, InfiniteData,
} from '@tanstack/react-query';
import { getActiveTripsData, getTrips } from '../../services/TripsService';
import { ActiveTripsData, TripDetails, TripsResponse } from '../../types/Trips';
import { FeaturesManagement } from '../../../featuresControl';
import { buildQueryPageParam, mergeNewestIntoFirstPage, withShowStandaloneAndMergedTrips } from '../../../utils/queryFilters';
import useGetTripMerge from './useGetTripMerge';

const hasOngoingTrips = (data?: InfiniteData<TripsResponse>): boolean => (
  data?.pages?.some((page) => page.results?.some((trip: TripDetails) => !trip.endAt)) ?? false
);

function useTripMergeQueryState(fleetId: string, enabled = true): {
  showStandaloneAndMergedTrips: boolean;
  isReady: boolean;
} {
  const { data: tripMerge, isFetched } = useGetTripMerge(fleetId, enabled);
  return {
    showStandaloneAndMergedTrips: !!tripMerge?.clientTripMergeEnabled,
    isReady: !enabled || isFetched,
  };
}

function withTripMergeParam(query: string, showStandaloneAndMergedTrips: boolean): string {
  return withShowStandaloneAndMergedTrips(query || '', showStandaloneAndMergedTrips);
}

function useGetBaseTrips(fleetId: string, query: string, enabled = true): UseQueryResult<TripsResponse> {
  const { showStandaloneAndMergedTrips, isReady } = useTripMergeQueryState(fleetId, enabled);
  const fullQuery = withTripMergeParam(query, showStandaloneAndMergedTrips);

  return useQuery(
    ['trips', fleetId, fullQuery],
    async (): Promise<TripsResponse> => getTrips(fleetId, fullQuery),
    {
      retry: 1,
      retryDelay: 3000,
      refetchInterval: FeaturesManagement.productivity.refetchInterval,
      keepPreviousData: true,
      enabled: enabled && isReady,
    },
  );
}

export function useGetBaseTripsLazy(fleetId: string, payload?: any, query?: string, deps?: any): UseInfiniteQueryResult<TripsResponse> {
  const queryClient = useQueryClient();
  const { showStandaloneAndMergedTrips, isReady } = useTripMergeQueryState(fleetId);
  const fullQuery = withTripMergeParam(query || '', showStandaloneAndMergedTrips);
  const queryKey = ['trips-lazy', fleetId, deps, fullQuery];

  const infiniteQuery = useInfiniteQuery(
    queryKey,
    async ({ pageParam }): Promise<TripsResponse> => {
      const pageQuery = withTripMergeParam(
        typeof pageParam === 'string' && pageParam ? pageParam : fullQuery,
        showStandaloneAndMergedTrips,
      );
      return getTrips(fleetId, pageQuery);
    },
    {
      retry: 1,
      retryDelay: 3000,
      keepPreviousData: true,
      cacheTime: 0,
      refetchOnWindowFocus: false,
      enabled: isReady,
      getNextPageParam: (lastPage) => {
        const nextSkip = lastPage.skip + lastPage.limit;
        if (nextSkip >= lastPage.total) return undefined;
        return buildQueryPageParam(fullQuery, nextSkip, lastPage.limit);
      },
    },
  );

  const { data: latestPage } = useQuery(
    ['trips-lazy-latest', fleetId, deps, fullQuery],
    async (): Promise<TripsResponse> => getTrips(fleetId, fullQuery),
    {
      retry: 1,
      retryDelay: 3000,
      refetchOnWindowFocus: false,
      refetchInterval: FeaturesManagement.productivity.refetchIntervalLazyLists,
      enabled: isReady && !!infiniteQuery.data,
    },
  );

  useEffect(() => {
    if (!latestPage) return;
    queryClient.setQueryData<InfiniteData<TripsResponse>>(
      queryKey,
      (existing) => mergeNewestIntoFirstPage(existing, latestPage),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [latestPage]);

  return {
    ...infiniteQuery,
    isLoading: !isReady || infiniteQuery.isLoading,
    isFetching: !isReady || infiniteQuery.isFetching,
  } as UseInfiniteQueryResult<TripsResponse>;
}

function useGetActiveTripsData(fleetId: string): UseQueryResult<ActiveTripsData> {
  const { showStandaloneAndMergedTrips, isReady } = useTripMergeQueryState(fleetId, !!fleetId);

  return useQuery(
    ['active-trips', fleetId, showStandaloneAndMergedTrips],
    async (): Promise<ActiveTripsData> => getActiveTripsData(fleetId, showStandaloneAndMergedTrips),
    {
      retry: 1,
      retryDelay: 3000,
      keepPreviousData: true,
      refetchInterval: FeaturesManagement.productivity.refetchInterval,
      enabled: !!fleetId && isReady,
    },
  );
}

function useGetTrips(fleetId: string, query: string, enabled = true): UseQueryResult<TripsResponse> {
  const {
    data: tripsData, isLoading: tripsDataLoading, isFetching: tripsDataIsFetching, ...rest
  } = useGetBaseTrips(fleetId, query, enabled);
  const {
    data: activeTripsData, isLoading: activeTripsLoading, isFetching: activeTripsIsFetching,
  } = useGetActiveTripsData(fleetId);

  const merged = tripsData?.results.map((trip: any) => {
    const activeTrip = activeTripsData?.[trip._id]
      || (trip?.mergedTripId ? activeTripsData?.[trip.mergedTripId] : undefined);
    return {
      ...trip,
      distanceInKm: trip?.endAt ? trip?.distanceInKm : activeTrip?.distanceInKm || trip?.distanceInKm,
      speedMs: trip?.endAt ? null : activeTrip?.speedMs,
    };
  });

  return {
    ...rest,
    data: { ...tripsData, results: merged } as TripsResponse,
    isLoading: tripsDataLoading || (activeTripsLoading && !tripsData),
    isFetching: tripsDataIsFetching || activeTripsIsFetching,
  } as UseQueryResult<TripsResponse>;
}

export function useGetTripsLazy(
  fleetId: string,
  payload?: any,
  query?: string,
  deps?: any,
): UseInfiniteQueryResult<TripsResponse> {
  const queryClient = useQueryClient();
  const { showStandaloneAndMergedTrips, isReady } = useTripMergeQueryState(fleetId);
  const fullQuery = withTripMergeParam(query || '', showStandaloneAndMergedTrips);
  const queryKey = ['active-trips-lazy', fleetId, deps, fullQuery];
  const {
    data: activeTripsData,
    isLoading: activeTripsLoading,
    isFetching: activeTripsFetching,
  } = useGetActiveTripsData(fleetId);

  const infiniteQuery = useInfiniteQuery(
    queryKey,
    async ({ pageParam }): Promise<TripsResponse> => {
      const pageQuery = withTripMergeParam(
        typeof pageParam === 'string' && pageParam ? pageParam : fullQuery,
        showStandaloneAndMergedTrips,
      );
      return getTrips(fleetId, pageQuery);
    },
    {
      retry: 1,
      retryDelay: 3000,
      keepPreviousData: true,
      cacheTime: 0,
      refetchOnWindowFocus: false,
      enabled: isReady,
      refetchInterval: (data) => (
        hasOngoingTrips(data) ? FeaturesManagement.productivity.refetchInterval : false
      ),
      getNextPageParam: (lastPage) => {
        const nextSkip = lastPage.skip + lastPage.limit;
        if (nextSkip >= lastPage.total) return undefined;
        return buildQueryPageParam(fullQuery, nextSkip, lastPage.limit);
      },
    },
  );

  const { data: latestPage } = useQuery(
    ['active-trips-lazy-latest', fleetId, deps, fullQuery],
    async (): Promise<TripsResponse> => getTrips(fleetId, fullQuery),
    {
      retry: 1,
      retryDelay: 3000,
      refetchOnWindowFocus: false,
      refetchInterval: () => (
        !hasOngoingTrips(infiniteQuery.data) && infiniteQuery.data
          ? FeaturesManagement.productivity.refetchIntervalLazyLists
          : false
      ),
      enabled: isReady && !!infiniteQuery.data,
    },
  );

  useEffect(() => {
    if (!latestPage || hasOngoingTrips(infiniteQuery.data)) return;
    queryClient.setQueryData<InfiniteData<TripsResponse>>(
      queryKey,
      (existing) => mergeNewestIntoFirstPage(existing, latestPage),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [latestPage]);

  const mergedPages = infiniteQuery.data?.pages.map((page) => ({
    ...page,
    results: page.results.map((trip) => {
      const activeTrip = activeTripsData?.[trip._id]
        || ((trip as any)?.mergedTripId ? activeTripsData?.[(trip as any).mergedTripId] : undefined);
      return {
        ...trip,
        distanceInKm: trip?.endAt ? trip?.distanceInKm : activeTrip?.distanceInKm || trip?.distanceInKm,
        speedMs: trip?.endAt ? null : activeTrip?.speedMs,
      };
    }),
  }));

  const isInitialLoading = !isReady
    || infiniteQuery.isLoading
    || (activeTripsLoading && !infiniteQuery.data);

  return {
    ...infiniteQuery,
    data: infiniteQuery.data
      ? {
        ...infiniteQuery.data,
        pages: mergedPages,
      }
      : undefined,
    isLoading: isInitialLoading,
    isFetching: !isReady || infiniteQuery.isFetching || activeTripsFetching,
  } as UseInfiniteQueryResult<TripsResponse>;
}

export default useGetTrips;
